import React, { useState, useEffect } from 'react';
import axios from 'axios';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';

const PageContainer = styled(motion.div)`
  padding: 4rem 2rem;
  background-color: #f0f4f8;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ContentWrapper = styled(motion.div)`
  background-color: white;
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(28, 47, 79, 0.1);
  padding: 3rem;
  width: 100%;
  max-width: 800px;

  @media (max-width: 768px) {
    padding: 2rem;
  }
`;

const Title = styled.h1`
  color: #1c2f4f;
  text-align: center;
  margin-bottom: 2rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const Input = styled.input`
  padding: 0.75rem;
  font-size: 1rem;
  border: 2px solid #e6eef8;
  border-radius: 8px;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: #1c2f4f;
  }
`;

const Button = styled(motion.button)`
  background-color: ${props => props.primary ? '#1c2f4f' : '#3a5a8f'};
  color: white;
  border: none;
  padding: 0.75rem;
  border-radius: 8px;
  cursor: pointer;
  font-size: 1rem;
  font-weight: bold;
  transition: background-color 0.3s ease;

  &:hover {
    background-color: ${props => props.primary ? '#2c3e50' : '#4a6fa5'};
  }

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;

const TimeInputContainer = styled.div`
  display: flex;
  gap: 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const TimeInput = styled(Input)`
  flex: 1;
`;

const EventList = styled(motion.ul)`
  list-style: none;
  padding: 0;
  margin-top: 2rem;
`;

const EventItem = styled(motion.li)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #e6eef8;
  color: #1c2f4f;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 0.5rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
`;

const Message = styled(motion.p)`
  text-align: center;
  margin-top: 1rem;
  padding: 0.5rem;
  border-radius: 8px;
  color: ${props => props.error ? '#e74c3c' : '#2ecc71'};
  background-color: ${props => props.error ? '#fadbd8' : '#d5f5e3'};
`;

const LoadingIndicator = styled.div`
  text-align: center;
  margin-top: 1rem;
  color: #1c2f4f;
`;

const UpdateAvailability = () => {
  const [tutorName, setTutorName] = useState('');
  const [date, setDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState(null);

  const fetchEvents = async () => {
    if (!tutorName) {
      setEvents([]);
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.get(`/api/availability/${tutorName}`);
      setEvents(response.data.events || []);
      setMessage(null);
    } catch (error) {
      console.error('Error fetching events:', error);
      setMessage({ text: 'Failed to fetch events. Please try again.', error: true });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, [tutorName]);

  const handleAddEvent = async (e) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      const response = await axios.post('/api/availability/add', {
        tutorName,
        date,
        startTime,
        endTime
      });

      if (response.status === 200) {
        setMessage({ text: 'Event added successfully!', error: false });
        fetchEvents();
        setDate('');
        setStartTime('');
        setEndTime('');
      }
    } catch (error) {
      console.error('Error adding event:', error);
      setMessage({ text: 'Failed to add event. Please try again.', error: true });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveEvent = async (eventId) => {
    if (!eventId) {
      setMessage({ text: 'Cannot remove event: Invalid event ID', error: true });
      return;
    }

    try {
      setIsLoading(true);
      const response = await axios.delete(`/api/availability/${tutorName}/${eventId}`);

      if (response.status === 200) {
        setMessage({ text: 'Event removed successfully!', error: false });
        fetchEvents();
      }
    } catch (error) {
      console.error('Error removing event:', error);
      setMessage({ text: 'Failed to remove event. Please try again.', error: true });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <PageContainer
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <ContentWrapper>
        <Title>Update Availability</Title>
        <Form onSubmit={handleAddEvent}>
          <Input
            type="text"
            value={tutorName}
            onChange={(e) => setTutorName(e.target.value)}
            placeholder="Tutor Name"
            required
          />
          <Input
            type="text"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            placeholder="Date (e.g., August 3, 2024)"
            required
          />
          <TimeInputContainer>
            <TimeInput
              type="time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              required
            />
            <TimeInput
              type="time"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              required
            />
          </TimeInputContainer>
          <Button
            type="submit"
            primary
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            disabled={isLoading}
          >
            Add Event
          </Button>
        </Form>

        {isLoading && <LoadingIndicator>Loading...</LoadingIndicator>}

        <AnimatePresence>
          {events.length > 0 && (
            <EventList
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              {events.map((event) => (
                <EventItem
                  key={event._id}
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  <span>{`${event.date}, ${event.startTime} - ${event.endTime}`}</span>
                  <Button
                    onClick={() => handleRemoveEvent(event._id)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    disabled={isLoading}
                  >
                    Remove
                  </Button>
                </EventItem>
              ))}
            </EventList>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {message && (
            <Message
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              error={message.error}
            >
              {message.text}
            </Message>
          )}
        </AnimatePresence>
      </ContentWrapper>
    </PageContainer>
  );
};

export default UpdateAvailability;